/**
 * SOLID Review
 * S — Strong. One concern: the frontend Application's boundary — its sockets, its lollipops, and the
 *     one build step that turns connected sockets into the contexts behind the lollipops.
 * O — Good. A new context is one more field built in `build()` and one more surface the provided
 *     holder forwards to.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two holders and a build step; the contexts are reached only through the lollipops.
 * D — Strong. The two holders and the context classes; every collaborator arrives through a socket.
 */
import { ComponentFrontendApplicationRequiredPorts } from './ComponentFrontendApplicationRequiredPorts';
import { ComponentFrontendApplicationProvidedPorts } from './ComponentFrontendApplicationProvidedPorts';
import { TableContext, type IConfigDepTableContext } from '../table/TableContext';
import { SessionContext, type IConfigDepSessionContext } from '../session/SessionContext';
import { AuthContext, type IConfigDepAuthContext } from '../auth/AuthContext';
import { LegalContext, type IConfigDepLegalContext } from '../legal/LegalContext';
import { PaymentContext, type IConfigDepPaymentContext } from '../payment/PaymentContext';
import { AdsContext, type IConfigDepAdsContext } from '../ads/AdsContext';
import { HealthContext, type IConfigDepHealthContext } from '../health/HealthContext';
import { MediaContext, type IConfigDepMediaContext } from '../media/MediaContext';
import { ClientSteps, type IConfigDepClientSteps } from '../shared/ClientSteps';

/**
 * THE FRONTEND APPLICATION AS A COMPONENT (frontend strict-ports plan, FE1, 2026-10-03), shaped exactly
 * like the backend's `ComponentApplication`: `MODEL_NAME`, two holder classes, and the three-phase
 * lifecycle — construct, then the composition root connects every socket, then `build()` reads them.
 *
 * The phases exist for the backend's reason. The provided holder must exist at construction, because
 * `describePorts` walks fields and Infrastructure's socket is connected to it. The contexts behind it
 * need the sockets connected first. So the lollipops forward at CALL time to what `build()` assigns.
 */
export class ComponentFrontendApplication {
  /** The model name in every generated diagram — one literal, so a typo cannot split this box in two. */
  static readonly MODEL_NAME = 'ComponentFrontendApplication';

  readonly requiredPorts = new ComponentFrontendApplicationRequiredPorts();
  /** Assigned in the constructor body: the holder takes this component, by its surfaces. */
  readonly providedPorts: ComponentFrontendApplicationProvidedPorts;

  private builtTable: TableContext | null = null;
  private builtSession: SessionContext | null = null;
  private builtAuth: AuthContext | null = null;
  private builtLegal: LegalContext | null = null;
  private builtPayment: PaymentContext | null = null;
  private builtAds: AdsContext | null = null;
  private builtHealth: HealthContext | null = null;
  private builtMedia: MediaContext | null = null;

  constructor() {
    this.providedPorts = new ComponentFrontendApplicationProvidedPorts(this);
  }

  /** The table loop's shells. Asked for before `build()` it is a composition-root defect, and says so. */
  get table(): TableContext {
    return built(this.builtTable);
  }

  /** The session's shells (FE2) — likewise only after `build()`. */
  get session(): SessionContext {
    return built(this.builtSession);
  }

  /** The account's shells (FE3) — likewise only after `build()`. */
  get auth(): AuthContext {
    return built(this.builtAuth);
  }

  /** The legal side's shells (FE4a) — likewise only after `build()`. */
  get legal(): LegalContext {
    return built(this.builtLegal);
  }

  /** The account's credits (FE4c) — likewise only after `build()`. */
  get payment(): PaymentContext {
    return built(this.builtPayment);
  }

  /** The ad slot (FE4d) — likewise only after `build()`. */
  get ads(): AdsContext {
    return built(this.builtAds);
  }

  /** The server's health (FE5a) — likewise only after `build()`. */
  get health(): HealthContext {
    return built(this.builtHealth);
  }

  /** The table's video chat (FE5b/FE5c) — likewise only after `build()`. */
  get media(): MediaContext {
    return built(this.builtMedia);
  }

  /**
   * Phase 3: read every socket — after assembly, so each resolves to its connected provider rather than
   * its fallback, and `assertAllPortsRead` can see that each one was read.
   */
  build(): void {
    const sockets = this.requiredPorts;
    const decisions = sockets.clientDecisionsSocket.getInterface();
    const state = sockets.clientStateSocket.getInterface();
    const rounds = sockets.clientRoundServerSocket.getInterface();
    const rooms = sockets.clientRoomServerSocket.getInterface();
    const tabStorage = sockets.clientTabStorageSocket.getInterface();
    // ONE serial section for both contexts: they write the same stores (FE2).
    const stepsDeps: IConfigDepClientSteps = { state, tabStorage };
    const steps = new ClientSteps(stepsDeps);
    const watch = sockets.clientServerWatchSocket.getInterface();
    const tableDeps: IConfigDepTableContext = { decisions, state, rounds, rooms, watch, steps };
    this.builtTable = new TableContext(tableDeps);
    const auth = sockets.clientAuthServerSocket.getInterface();
    const authDeps: IConfigDepAuthContext = { decisions, auth, steps };
    const account = new AuthContext(authDeps);
    this.builtAuth = account;
    const sessionDeps: IConfigDepSessionContext = {
      decisions, tabStorage, rooms, rounds, steps, auth, account,
      players: sockets.clientPlayerServerSocket.getInterface(),
    };
    this.builtSession = new SessionContext(sessionDeps);
    const legalDeps: IConfigDepLegalContext = {
      decisions, steps, account,
      legal: sockets.clientLegalServerSocket.getInterface(),
      consent: sockets.clientConsentPlatformSocket.getInterface(),
    };
    this.builtLegal = new LegalContext(legalDeps);
    const paymentDeps: IConfigDepPaymentContext = { decisions, steps, watch, payments: sockets.clientPaymentServerSocket.getInterface() };
    this.builtPayment = new PaymentContext(paymentDeps);
    const adsDeps: IConfigDepAdsContext = { decisions, steps, ads: sockets.clientAdsServerSocket.getInterface() };
    this.builtAds = new AdsContext(adsDeps);
    const healthDeps: IConfigDepHealthContext = { health: sockets.clientServerHealthSocket.getInterface() };
    this.builtHealth = new HealthContext(healthDeps);
    const mediaDeps: IConfigDepMediaContext = { decisions, steps, media: sockets.clientMediaServerSocket.getInterface() };
    this.builtMedia = new MediaContext(mediaDeps);
  }
}

function built<T>(context: T | null): T {
  if (context === null) {
    throw new Error('ComponentFrontendApplication: a use case was called before build() — assemble the graph, then build.');
  }
  return context;
}
